import {
  PlanRateLimit,
  SubscriptionPackage,
  UsageStats,
} from '@/renderer/types/billing';

const billingPlanDisplayNames: Record<SubscriptionPackage, string> = {
  starter: 'smartPOS Starter',
  professional: 'smartPOS Professional',
  enterprise: 'smartPOS Enterprise',
  pro_semi_annual: 'smartPOS Pro Semi-Annual',
};

const formatCount = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

export interface BillingPlanGuardResult {
  isAllowed: boolean;
  message: string | null;
}

export const getBillingPlanDisplayName = (packageId: SubscriptionPackage) =>
  billingPlanDisplayNames[packageId];

export const getBillingPlanGuard = (
  usage: UsageStats | null,
  packageId: SubscriptionPackage,
  planRateLimits: Record<string, PlanRateLimit>,
): BillingPlanGuardResult => {
  if (!usage) return { isAllowed: true, message: null };

  const rateLimit = planRateLimits[packageId];

  // No rate limit record or enterprise (all nulls) → always allowed
  if (
    !rateLimit ||
    (rateLimit.max_outlets === null &&
      rateLimit.max_users === null &&
      rateLimit.max_users_per_outlet === null)
  ) {
    return { isAllowed: true, message: null };
  }

  const outletCount = usage.outlets.used;
  const userCount = usage.users.used;
  const planName = getBillingPlanDisplayName(packageId);

  // Check outlet limit
  if (rateLimit.max_outlets !== null && outletCount > rateLimit.max_outlets) {
    const userNote =
      rateLimit.max_users_per_outlet != null
        ? ` and ${rateLimit.max_users_per_outlet} users per outlet`
        : rateLimit.max_users != null
          ? ` and up to ${rateLimit.max_users} users`
          : '';
    return {
      isAllowed: false,
      message: `${planName} supports up to ${formatCount(rateLimit.max_outlets, 'outlet', 'outlets')}${userNote}. Your company currently has ${formatCount(outletCount, 'outlet', 'outlets')}. Reduce your outlet count before switching.`,
    };
  }

  // Check per-outlet user limit (e.g. professional: 50 users per outlet)
  if (rateLimit.max_users_per_outlet !== null) {
    const userLimit = Math.max(outletCount, 1) * rateLimit.max_users_per_outlet;
    if (userCount > userLimit) {
      return {
        isAllowed: false,
        message: `${planName} supports up to ${rateLimit.max_users_per_outlet} users per outlet (${userLimit} users across ${formatCount(Math.max(outletCount, 1), 'outlet', 'outlets')}). Your company currently has ${formatCount(userCount, 'user', 'users')}. Reduce usage before switching.`,
      };
    }
  }

  // Check absolute user limit (e.g. starter: 5 users total)
  if (rateLimit.max_users !== null && userCount > rateLimit.max_users) {
    return {
      isAllowed: false,
      message: `${planName} supports up to ${rateLimit.max_users} users. Your company currently has ${formatCount(userCount, 'user', 'users')}. Reduce usage before switching.`,
    };
  }

  return { isAllowed: true, message: null };
};
